import { Observable } from 'rxjs';
import { Component, Injectable, OnInit, AfterViewInit, ViewChildren, QueryList, ChangeDetectorRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LoadingPanelService, LoadingPanelSettings } from '../shared/loading-panel/loading-panel.service';
import { GenericModalService } from '../shared/generic-modal/generic-modal.service';
import { StateService } from '../shared/state-management/state.service';
import { User } from '../user/models/user';
import { TagsService } from '../tags/services/tags.service';
import { TagsUsage } from '../tags/models';
import { PartnerService } from './services/partner.service';
import { PartnerExportUiService } from './services/partner-export-ui.service';
import { Partner } from './models/partner';
import { CurrentUserService } from '../user/services';
import { PartnerComponentBase } from './partner-search-details.base';
import { PartnerFavoritesService, SavedPartnerFavorite } from './services/partner-favorites.service';
import { ConfigurationService } from '../configuration/services/configuration.service';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../shared/change-log/change-log-link-entity/change-log-link-entity.directive';

import { FormsModule } from '@angular/forms';
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap';
import { PartnerSearchDetailsGeneralComponent } from './partner-search-details-general.component';
import { PartnerSearchDetailsConfigurationComponent } from './partner-search-details-configuration.component';
import { PartnerSearchDetailsIdentificationComponent } from './partner-search-details-identifications.component';
import { PartnerSearchDetailsContactsComponent } from './partner-search-details-contacts.component';
import { PartnerSearchDetailsConfigurationKeysComponent } from './partner-search-details-configuration-keys.component';
import { PartnerSearchDetailsLocationsComponent } from './partner-search-details-locations.component';
import { PartnerSearchDetailsConversionTablesComponent } from './partner-search-details-conversiontables.component';
import { PartnerSearchDetailsFtpComponent } from './partner-search-details-ftp.component';
import { PartnerSearchDetailsTagsComponent } from './partner-search-details-tags.component';
import { PartnerSearchDetailsCertificatesComponent } from './partner-search-details-certificates.component';
import { PartnerSearchDetailsProxyPartnersComponent } from './partner-search-details-proxy-partners.component';
import { PartnerSearchDetailsOnboardingComponent } from './partner-search-details-onboarding.component';
import { NewPartnerComponent } from './new-partner/new-partner.component';
import { PartnerOnboardingService } from './services/partner-onboarding.service';
import { PartnerSearchDetailsOnboardingDetailsComponent } from './partner-search-details-onboarding-details.component';
import { PartnerOnboarding, PartnerOnboardingStatus } from '../partnerportal/models/partner-onboarding';

@Component({
    templateUrl: './partner-search-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, FormsModule, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, PartnerSearchDetailsGeneralComponent, PartnerSearchDetailsConfigurationComponent, PartnerSearchDetailsIdentificationComponent, PartnerSearchDetailsContactsComponent, PartnerSearchDetailsConfigurationKeysComponent, PartnerSearchDetailsLocationsComponent, PartnerSearchDetailsConversionTablesComponent, PartnerSearchDetailsFtpComponent, PartnerSearchDetailsTagsComponent, PartnerSearchDetailsCertificatesComponent, PartnerSearchDetailsProxyPartnersComponent, NewPartnerComponent, PartnerSearchDetailsOnboardingComponent, PartnerSearchDetailsOnboardingDetailsComponent]
})
export class PartnerSearchDetailsComponent implements OnInit, AfterViewInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private configService = inject(ConfigurationService);
    private currentUserService = inject(CurrentUserService);
    private partnerService = inject(PartnerService);
    private tagsService = inject(TagsService);
    private stateService = inject(StateService);
    private modalService = inject(GenericModalService);
    private loadingPanelService = inject(LoadingPanelService);
    private cdRef = inject(ChangeDetectorRef);
    private partnerExportService = inject(PartnerExportUiService);
    private partnerFavoritesService = inject(PartnerFavoritesService);

    public isEditing = false;
    public selectedTab = 'general';
    public partnerIds: number[] = [];
    public partners: Partner[] = [];
    public selectedPartner: Partner;
    public newlyCreatedPartner: Partner = null;
    public isCreatingNew = false;
    public isCreatingNewOnboarding = false;
    public showProxyPartner = false;
    public showTags = false;
    public showOnbording = false;
    public openOnLocation = false;

    public partnerOnboarding: PartnerOnboarding = null;

    @ViewChildren('partnerComponentGeneral,partnerComponentOnboarding,partnerComponentIdentifications,partnerComponentConfigurations,partnerComponentContacts,partnerComponentLocations,partnerComponentConversions,partnerComponentConfigurationKeys,partnerComponentFtp,partnerComponentTags,partnerComponentCertificates,partnerComponentProxyPartners,partnerComponentOnboardingDetails') private partnerComponents: QueryList<PartnerComponentBase>;

    constructor(private partnerOnboardingService: PartnerOnboardingService){  }

    ngOnInit(): void {
        this.showOnbording = this.currentUserService.hasPermission('Partner_View_Onboarding');
        void this.InitAsync();
    }

    async InitAsync(): Promise<void> {
        this.route.params.subscribe((parms: Params) => {
            if (parms.openOnLocation) {
                this.openOnLocation = true;
            }
            if (parms.partnerIds) {
                const partnerIds = parms.partnerIds as string;
                this.partnerIds = partnerIds.split(',').map(Number);

                // Make sure we don't load more partners than allowed...
                if (this.partnerIds.length > PartnerService.maxNumberOfPartnersToShow) {
                    this.partnerIds = this.partnerIds.slice(0, PartnerService.maxNumberOfPartnersToShow);
                }

                this.refresh(true); 

                if (parms.showTransportConfiguration) {
                    this.selectedTab = 'locations';
                }
            }
            else if (parms.createNew) {
                this.createNew();
            }
            else if (parms.createNewOnboarding) {
                this.createNewOnboarding();
            }
        });

        if (this.currentUserService.hasPermission('Partner_View_Tags') && (await this.tagsService.getTags(TagsUsage.Partner))?.length > 0 ) 
        {
            this.showTags = true;
        }

        this.showProxyPartner = this.currentUserService.hasPermission('Partner_Action_ProxyPartnerShow');
    }

    ngAfterViewInit() {
        this.partnerComponents.forEach(pc => {
            pc.editModeObserver.subscribe(isEditing => this.isEditing = isEditing);
            pc.refreshDataObserver.subscribe(() => this.refreshSearchData());
            pc.reloadDataObserver.subscribe(() => this.refresh(false));
        });
    }

    public evaluatePermissions(permission: string, user: User, hasPermission: boolean, partner: Partner) {
        if (permission === 'Partner_Action_InternalPartner') {
            if (partner && partner.PartnerType && partner.PartnerType.Name !== 'Internal') {
                // For non-internal partners, ignore the 'internal' permission by always granting it.
                hasPermission = true;
            }
        }

        return hasPermission;
    }

    /**
     * Tells the search-page to refresh data whenever it's shown again.
     */
    refreshSearchData() {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const stateObject = this.stateService.getState('partner-search-component-result-state');
        if (stateObject) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            stateObject.refreshData = true;
        }
    }

    async refresh(detectChanges: boolean = false, allowTabChange = true): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Loading partners...'));

            const partners = await this.partnerService.getPartners(this.partnerIds);
            if (partners) {
                this.partners = partners.sort((p1, p2) => p1.PartnerName.localeCompare(p2.PartnerName));

                if (this.newlyCreatedPartner) {
                    this.selectedPartner = this.newlyCreatedPartner;
                    this.newlyCreatedPartner = null;
                } else {
                    const currentSelectedPartnerId = this.selectedPartner?.PartnersId;
                    const refreshedSelectedPartner = currentSelectedPartnerId
                        ? this.partners.find(partner => partner.PartnersId === currentSelectedPartnerId)
                        : null;

                    this.selectedPartner = refreshedSelectedPartner ?? this.partners[0];
                }

                if (!this.selectedPartner) {
                    await this.router.navigate(['partner']);
                }

                if(this.showOnbording){
                    this.partnerOnboarding = await this.partnerOnboardingService.getPartnerOnboarding(this.selectedPartner?.PartnersId);
                }
                
                this.loadingPanelService.hideLoadingPanel(); // Hide it here because selectedPartnerChanged method shows it again when loading details...
                this.selectedPartnerChanged(this.selectedPartner);
                if (this.openOnLocation === true) {
                    this.selectedTab = 'locations';
                }
                else if (this.partnerOnboarding && this.partnerOnboarding.Status !== PartnerOnboardingStatus.Done && allowTabChange) {
                    this.selectedTab = 'onboarding-details';
                }

                if (detectChanges) { // This is necessary if it's a double click from the dev-express grid, which triggered this page to show.
                    this.cdRef.detectChanges();
                }
            }
        } catch (err) {
            console.error('Error loading partners', err);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    createNew() {
        this.isCreatingNew = true;
        this.isCreatingNewOnboarding = false;
    }

    createNewOnboarding() {
        this.isCreatingNewOnboarding = true;
        this.isCreatingNew = false;
    }

    newPartnerSaved(partner: Partner) {
        this.isCreatingNew = false;
        this.isCreatingNewOnboarding = false;
        this.newlyCreatedPartner = partner;
        this.partnerIds.push(partner.PartnersId);
        this.refreshSearchData();
        void this.router.navigate(['partner/details', { partnerIds: this.partnerIds.join(',') }]); // Refresh page to get the correct URL and remove the createNew parameter. Notice: this 'refresh' doesn't reinitialize the component, but just trigger the route.params subscribtion!
    }

    newPartnerCancelled() {
        this.isCreatingNew = false;
        this.isCreatingNewOnboarding = false;
        if (this.partners.length === 0) {
            void this.router.navigate(['partner']);
        }
    }

    export() {
        if (this.selectedPartner) {
            void this.partnerExportService.exportPartners([this.selectedPartner.PartnersId]);
        }
    }

    async delete() {
        if (this.selectedPartner) {
            let deleteMessage = `Delete ${this.selectedPartner.PartnerName}`;
            if (this.selectedPartner.IsProxyPartner === true) {
                const proxyConnections = await this.partnerService.getProxyPartnerConnections(this.selectedPartner.PartnersId);
                if (proxyConnections.length !== 0) {
                    deleteMessage = deleteMessage + ' and remove the following connections:</br>'
                } else {
                    deleteMessage = deleteMessage + '?'
                }
                for (const connection of proxyConnections) {
                    deleteMessage = `${deleteMessage}${connection.PartnerName}</br>`;
                }
            }

            this.modalService.confirmDirect('Delete', deleteMessage).then(() => {
                this.loadingPanelService.showLoadingPanelDirect('Delete partner...');
                void this.partnerService.deletePartner(this.selectedPartner.PartnersId).then(() => {
                    this.loadingPanelService.hideLoadingPanel();

                    const partnerIndex = this.partners.indexOf(this.selectedPartner);
                    const partnerIdIndex = this.partnerIds.indexOf(this.selectedPartner.PartnersId);

                    this.partners.splice(partnerIndex, 1);
                    this.partnerIds.splice(partnerIdIndex, 1);

                    this.refreshSearchData();

                    if (this.partners.length > 0) {
                        this.selectedPartnerChanged(this.partners[0]);
                    }
                    else {
                        void this.router.navigate(['partner']);
                    }
                });
            }).catch(() => { });

        }
    }

    removePartner(partner: Partner) {
        const index = this.partnerIds.indexOf(partner.PartnersId);
        if (index >= 0) {
            this.partnerIds.splice(index, 1);
        }

        void this.router.navigate(['partner/details', { partnerIds: this.partnerIds.join(',') }]); // Refresh page to get the correct URL and remove the createNew parameter. Notice: this 'refresh' doesn't reinitialize the component, but just trigger the route.params subscribtion!
        this.refresh();
    }

    selectedPartnerChanged(partner: Partner) {
        this.selectedPartner = partner;
        this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Loading partner details...'));
        const partnerSetPromises = this.partnerComponents.map(pc => pc.setPartner(this.selectedPartner));
        void Promise.all(partnerSetPromises).then(() => this.loadingPanelService.hideLoadingPanel());
    }

    private justLeave = false;
    canDeactivate() {
        if (!this.justLeave && (this.isEditing || this.isCreatingNew)) {
            return this.modalService.confirmDirect('Not saved', 'You have not saved your changes. Continue?').then(() => { this.justLeave = true; return true; }).catch(() => false);
        }
        return true;
    }

    addToFavorite() {
        void this.partnerFavoritesService.saveSearch(new SavedPartnerFavorite(this.selectedPartner.PartnerName, { partnerIds: this.selectedPartner.PartnersId })).then(() => {
            this.modalService.messageDirect('Saved', 'The partner is now added to Favorites and will be available directly from the main menu').then(() => { }).catch(() => { });
        });
    }

    onChangeTab(tabId: string) {
        this.selectedTab = tabId;
        this.refresh(false, false);
    }
}


@Injectable({
    providedIn: 'root'
})
export class PartnerSearchDetailsCanDeactivate {
    canDeactivate(component: PartnerSearchDetailsComponent): Observable<boolean> | Promise<boolean> | boolean {
        return component.canDeactivate();
    }
}
